/**
 * Platform-agnostic scoring types shared by web, mobile, and API.
 */

export interface ValidationError {
  field?: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Context needed to resolve show-type-specific behaviour that depends on more
 * than the show_type alone (e.g. agility differs by sanctioning club).
 */
export interface ScoringContext {
  sanctioningClub?: string;
  /** The score's dog_class (element / level / section). */
  dogClass?: {
    show_element?: string;
    level?: string;
    section?: string;
  };
  /** The parent show, when available (used for element config like num_hides). */
  show?: any;
}

/**
 * A scoring strategy encapsulates everything platform-agnostic about entering
 * results for a given show type: which API endpoint to hit, and how to validate
 * the entered result before saving. This is the single source of truth consumed
 * by both the web Results tab and the mobile Scoring screens.
 */
export interface ScoringStrategy {
  /** The show_type value (Constants.show_type.*). */
  showType: string;

  /**
   * Endpoint suffix for PUT /api/events/:eventId/scores/:scoreId/{apiPath}.
   * A function when the path depends on context (e.g. agility by club).
   */
  apiPath: string | ((ctx: ScoringContext) => string);

  /**
   * Validate the entered results object prior to save. Returns the list of
   * validation errors (empty when valid). Scratch / conflict selections are
   * handled separately by the caller and bypass this validation.
   */
  validate?: (results: any, ctx: ScoringContext) => ValidationResult;

  /** True when results require explicit judge approval + creds (fetch, farm dog). */
  requiresJudgeApproval?: boolean;

  /** True when this show type stores an array of results (scentwork multi-hide). */
  isMultiResult?: boolean;
}

export const ok = (): ValidationResult => ({ valid: true, errors: [] });

export const fail = (
  message: string,
  field?: string
): ValidationResult => ({
  valid: false,
  errors: [{ message, field }],
});
